<template>
  <div
    class="sql-view-data-grid"
    :class="{ 'sql-view-data-grid--dashboard': dashboardStyle }"
    ref="gridWrapper">
    <!-- Custom Dashboard toolbar: the approved Structured Register treatment. -->
    <div v-if="dashboardStyle" class="sql-view-data-grid-toolbar sql-grid-operations">
      <v-text-field
        v-model="searchText"
        class="sql-grid-search"
        placeholder="Search this grid"
        dense
        outlined
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        @input="onGridSearchInput">
      </v-text-field>

      <v-menu bottom offset-y :close-on-content-click="false">
        <template #activator="{ on, attrs }">
          <v-btn
            class="sql-grid-row-hint"
            icon
            small
            v-bind="attrs"
            aria-label="How to view row details"
            v-on="on">
            <v-icon size="18" aria-hidden="true">mdi-information-outline</v-icon>
          </v-btn>
        </template>
        <v-card class="pa-3" max-width="300">Double-click a record, or select it and press Enter, to view its full details.</v-card>
      </v-menu>

      <div class="sql-grid-actions">
        <v-btn
          v-if="bulkSpatialAvailable"
          class="sql-grid-toolbar-btn sql-grid-isolate-btn"
          outlined
          :loading="isolateFilteredLoading"
          title="Show available locations for the filtered records in 3D. Records without a location are not shown."
          @click="isolateFilteredRows">
          <v-icon small>mdi-cube</v-icon>
          <span class="sql-grid-action-label">Isolate filtered</span>
        </v-btn>

        <v-btn
          v-if="exportEnabled"
          class="sql-grid-toolbar-btn"
          outlined
          :loading="exporting"
          @click="exportData">
          <v-icon small>mdi-download-outline</v-icon>
          <span class="sql-grid-action-label">Export CSV</span>
        </v-btn>

        <v-menu
          offset-y
          left
          :close-on-content-click="false"
          content-class="sql-grid-queries-menu">
          <template #activator="{ on, attrs }">
            <v-btn
              class="sql-grid-toolbar-btn sql-grid-queries-btn"
              outlined
              v-bind="attrs"
              :aria-label="queriesButtonLabel"
              v-on="on">
              <v-icon small>mdi-filter-variant</v-icon>
              <span class="sql-grid-action-label">Filters applied</span>
              <span v-if="scopeChips.length" class="sql-grid-queries-count" aria-hidden="true">{{ scopeChips.length }}</span>
            </v-btn>
          </template>

          <v-list dense class="sql-grid-queries-list" min-width="244" max-width="320">
            <v-subheader class="sql-grid-queries-subheader">Filters applied</v-subheader>

            <v-list-item v-if="scopeChips.length === 0" disabled>
              <v-list-item-content>
                <v-list-item-title class="sql-grid-queries-empty">No local search or widget filters</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-for="chip in scopeChips" :key="chip.key" class="sql-grid-queries-item">
              <v-list-item-content>
                <v-list-item-title>{{ chip.label }}</v-list-item-title>
                <v-list-item-subtitle v-if="chip.hint">{{ chip.hint }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  v-if="chip.removable"
                  icon
                  x-small
                  :aria-label="'Remove ' + chip.label"
                  @click="removeScopeChip(chip)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
                <v-icon v-else small class="sql-grid-queries-lock" aria-hidden="true">mdi-lock-outline</v-icon>
              </v-list-item-action>
            </v-list-item>

            <template v-if="hasClearableScope">
              <v-divider></v-divider>
              <v-list-item @click="clearLocalScope">
                <v-list-item-icon>
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Clear local scope</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Existing compact toolbar for non-Custom-Dashboard consumers. -->
    <div v-else class="sql-view-data-grid-toolbar d-flex align-center justify-space-between mb-1">
      <v-text-field
        v-model="searchText"
        placeholder="Search..."
        dense
        outlined
        hide-details
        clearable
        append-icon="mdi-magnify"
        style="max-width: 280px"
        @input="onGridSearchInput">
      </v-text-field>

      <div class="d-flex align-center" style="gap: 8px">
        <v-btn
          v-if="canIsolateSelectedRow"
          small
          outlined
          color="primary"
          @click="isolateSelectedRow">
          <v-icon small class="mr-1">mdi-cube-scan</v-icon>
          Isolate in 3D
        </v-btn>

        <v-btn
          v-if="bulkSpatialAvailable"
          small
          outlined
          color="primary"
          :loading="isolateFilteredLoading"
          title="Show available locations for the filtered records in 3D. Records without a location are not shown."
          @click="isolateFilteredRows">
          <v-icon small class="mr-1">mdi-cube</v-icon>
          Isolate filtered
        </v-btn>

        <v-btn
          v-if="exportEnabled"
          small
          outlined
          color="primary"
          :loading="exporting"
          @click="exportData">
          <v-icon small class="mr-1">mdi-download</v-icon>
          Export
        </v-btn>
      </div>
    </div>

    <!-- AG Grid + a rows-only loading layer. The persistent header, pager, and all
         controls stay mounted and interactive while a datasource request resolves. -->
    <div
      class="sql-view-ag-grid-stack"
      :class="{ 'sql-view-ag-grid-stack--structured': dashboardStyle }"
      :style="gridHeight ? { height: gridHeight + 'px' } : {}"
      aria-live="polite">
      <ag-grid-vue
        class="sql-view-ag-grid"
        :class="[
          $vuetify.theme.isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine',
          { 'sql-view-ag-grid--structured': dashboardStyle },
          gridSizeClass
        ]"
        :columnDefs="columnDefs"
        :defaultColDef="resolvedDefaultColDef"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="2"
        :maxConcurrentDatasourceRequests="1"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="10"
        :pagination="true"
        :paginationPageSize="cacheBlockSize"
        :tooltipShowDelay="350"
        :overlayNoRowsTemplate="resolvedNoRowsTemplate"
        :headerHeight="dashboardStyle ? sizeHeaderHeight : undefined"
        :rowHeight="dashboardStyle ? sizeRowHeight : undefined"
        rowSelection="single"
        :suppressRowClickSelection="false"
        @grid-ready="onGridReady"
        @sort-changed="onSortChanged"
        @column-resized="onColumnResized"
        @column-moved="onColumnMoved"
        @row-clicked="onRowClicked"
        @cell-double-clicked="onRowDoubleClicked"
        @cell-key-down="onCellKeyDown">
      </ag-grid-vue>

      <!-- Rows skeleton = FIRST load only. A refetch on a grid that has already settled
           (cross-filter/session/widget-filter change) keeps the current rows mounted
           (refreshInfiniteCache) and overlays the scrim + progress strip instead — same
           refresh-with-data presentation the chart widgets use. -->
      <dashboard-widget-skeleton
        v-if="gridLoading && !refreshing && !gridSoftRefresh"
        class="sql-grid-skeleton-overlay"
        widget-type="datagrid"
        :show-header="false"
        :show-table-toolbar="false"
        table-body-only />
      <template v-if="gridSoftRefresh && gridLoading">
        <div class="svdg-refresh-scrim" aria-hidden="true"></div>
        <v-progress-linear indeterminate color="primary" height="2" class="svdg-refresh-bar" />
      </template>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import ViewerClickCellRenderer from './sis-datagrid/viewer-click-cell-renderer.vue';
import DashboardWidgetSkeleton from './DashboardWidgetSkeleton.vue';
import apiService from '../service/dashboard-widgets.service';
import { WIDGET_SIZE } from '@/features/Dashboard/utils/widgetSize';
import { formatHeaderName } from '@/features/Dashboard/utils/columnLabels';
import { columnColorRule, columnStatusStyle } from '@/features/Dashboard/utils/columnColoring';
import { DATE_SQL_TYPES } from '@/features/Dashboard/utils/columnTypes';
import { log, warn, error } from '@/utils/debugLogger';
import {
  REGISTER_NOT_CONFIGURED_COPY,
  isRegisterConfigurationRequiredError,
} from '@/utils/registerConfigurationError';
import { clearThemingColors, UNMAPPED_ISOLATE_COLOR } from '@/utils/forgeUtils';
import {
  buildSpatialQueryScope,
  buildSpatialRowScope,
  deriveGridRowWindow,
  hasExactSpatialCapability,
  spatialRowKeyValue,
} from '@/features/Dashboard/utils/dashboardSelectionScope';
import {
  buildSpatialResolveRequest,
  legacySpatialFocusOverride,
  spatialFocusFallbackEnabled,
  resolveSpatialFocusSessionPolicy,
} from '@/features/Dashboard/utils/spatialFocusPolicy';
import { useSpatialFocus } from '@/features/Dashboard/composables/useSpatialFocus';
import {
  resolveTagLink,
  rowCanIsolate,
  ROW_SPATIAL_METADATA,
} from '@/features/Dashboard/utils/tagLink';
import { debounce } from 'lodash';
import {
  focusOutcomeMessage,
  filterConditionLabel,
  hasDisplayTarget,
  spatialRefusalMessage,
} from '@/features/Dashboard/utils/dashboardReader';

// Locally-owned scope-chip keys shared by the queries menu producer (scopeChips) and its
// remove handler (removeScopeChip). Only these three are removable here; saved-filters and
// dashboard scope are owned elsewhere and stay out of this map.
const SCOPE_CHIP = Object.freeze({ SEARCH: 'search', FILTERS: 'filters', SORT: 'sort' });
const VIEWER_INTERACTION_SUPPRESSION_MS = 1000;

export default {
  name: 'SqlViewDataGrid',

  components: {
    AgGridVue,
    ViewerClickCellRenderer,
    DashboardWidgetSkeleton,
  },

  props: {
    /** The widget message object containing content data */
    message: {
      type: Object,
      required: true,
    },
    /** Parsed sqlViewConfig */
    sqlViewConfig: {
      type: Object,
      default: null,
    },
    /** Global dashboard-level filters (plant, unit, code, etc.) */
    globalFilters: {
      type: Object,
      default: () => ({}),
    },
    /** Server-side filter session ID (replaces sending full filter payloads) */
    filterSessionId: {
      type: String,
      default: null,
    },
    /** Empty-match (decision 10): the assigned filter session was created with 0 tags, so the
     *  combined chart selection matches nothing. The datasource returns an empty page instead
     *  of the unfiltered rows the api sends alongside 'session-expired'. */
    sessionEmptyMatch: {
      type: Boolean,
      default: false,
    },
    /** Widget-level runtime filters from WidgetFilterMenu */
    widgetFilters: {
      type: Object,
      default: null,
    },
    /** TRANSIENT same-table cross-filter predicates (dashboardSelectionScope). Same-table
     *  dimensions are exact raw filters, never a tag session: many rows can share one Master Tag,
     *  so a tag intersection shows MORE rows than the bar the user clicked. Merged into the query
     *  payload at build time and never written into the widget's own filter state. */
    selectionFilters: {
      type: Array,
      default: () => [],
    },
    /** True when this widget's table is past the cross-table session cap. It has no external-only
     *  session, so re-querying would fall back to the FULL session — which still carries this
     *  table's own dimensions tag-mediated and would show more rows than the selection. Keep the
     *  current rows and let the reference badge say so instead. */
    frozen: {
      type: Boolean,
      default: false,
    },
    /** Opts the live Custom Dashboard widget into the approved Structured Register UI. */
    dashboardStyle: {
      type: Boolean,
      default: false,
    },
    /** Density level ('small' | 'normal' | 'large'). LARGE is the family default: the AG grid was
     *  designed at the large density, so an unset/legacy grid keeps today's exact row/header/font
     *  metrics. Only consumed when dashboardStyle is true. */
    widgetSize: {
      type: String,
      default: WIDGET_SIZE.LARGE,
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    refreshing: {
      type: Boolean,
      default: false,
    },
    isolationClaim: {
      type: Object,
      default: null,
    },
    isClaimCurrent: {
      type: Function,
      default: () => true,
    },
    claimIsolation: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      gridApi: null,
      columnApi: null,
      searchText: '',
      exporting: false,
      gridLoading: false,
      // First-settle latch + soft-refresh flag: after the first datasource result lands, later
      // refetches keep the rendered rows and show the scrim/strip instead of the rows skeleton.
      gridHasSettled: false,
      gridSoftRefresh: false,
      allColumnNames: [],
      sortColumn: null,
      sortDirection: 'asc',
      runtimeFilters: [],
      selectedRowData: null,
      suppressRowDetailsUntil: 0,
      // Set true by "Isolate All" before opening the viewer; consumed in isolateSelectedTag
      // so the post-load callback runs the all-rows path instead of the single-row one.
      pendingIsolateAll: false,
      pendingIsolateAllToken: null,
      pendingFilteredSpatial: null,
      isolationIntentSeq: 0,
      isolateFilteredLoading: false,
      filteredSpatialSeq: 0,
      spatialCapabilitySeq: 0,
      sourceSpatialStatus: 'unknown',
      loadedSpatialEligibility: 'unknown',
      // The server-owned spatial capability, hydrated from the catalogue for a widget saved before
      // the contract existed. Reactive because `effectiveSpatial` renders from it.
      hydratedSpatial: null,
      gridHeight: null,
      resizeObserver: null,
      rowModelType: 'infinite',
      cacheBlockSize: 100,
      infiniteInitialRowCount: 100,
      noRowsTemplate: '<span class="text--disabled">No data available</span>',
      // AVA-602: the source is a Facility Group register with rows but no Shared/Separated mode.
      // Drives the no-rows overlay copy; cleared by the next successful page.
      configurationRequired: false,
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false,
        minWidth: 100,
        flex: 1,
      },
    };
  },

  computed: {
    // Density variant class for the structured grid. LARGE == the base .sql-view-ag-grid--structured
    // values (today), so it carries NO extra class and stays byte-identical; small/normal add an
    // override pack. Only meaningful under dashboardStyle (the mini / non-dashboard grid is untouched).
    gridSizeClass() {
      if (!this.dashboardStyle) return null;
      if (this.widgetSize === WIDGET_SIZE.SMALL) return 'sql-view-ag-grid-size--small';
      if (this.widgetSize === WIDGET_SIZE.NORMAL) return 'sql-view-ag-grid-size--normal';
      return null; // large / unset -> base pack (today's verbatim metrics)
    },
    // AG rowHeight per density; LARGE == today's 50 so the default grid is byte-identical. AG derives
    // the row line-height from this, so no explicit --ag-line-height is needed (same as today).
    sizeRowHeight() {
      if (this.widgetSize === WIDGET_SIZE.SMALL) return 33;
      if (this.widgetSize === WIDGET_SIZE.NORMAL) return 41;
      return 50; // large / unset
    },
    // AG headerHeight per density; LARGE == today's 45.
    sizeHeaderHeight() {
      if (this.widgetSize === WIDGET_SIZE.SMALL) return 32;
      if (this.widgetSize === WIDGET_SIZE.NORMAL) return 38;
      return 45; // large / unset
    },
    resolvedNoRowsTemplate() {
      if (this.configurationRequired) {
        // Static copy only (no server text), so the HTML template string is safe.
        return `<div class="sql-grid-empty-state" role="status">
          <span class="sql-grid-empty-state__icon mdi mdi-table-cog" aria-hidden="true"></span>
          <strong>${REGISTER_NOT_CONFIGURED_COPY.title}</strong>
          <span>${REGISTER_NOT_CONFIGURED_COPY.message}</span>
        </div>`;
      }
      if (!this.dashboardStyle) return this.noRowsTemplate;
      const scoped = !!(this.searchText || Number(this.activeFilterCount) > 0);
      const title = scoped ? 'No matching rows' : 'No rows available';
      const detail = scoped
        ? 'Clear the local scope or adjust the search to see more results.'
        : 'This register did not return any records.';
      return `<div class="sql-grid-empty-state" role="status">
        <span class="sql-grid-empty-state__icon mdi mdi-table-search" aria-hidden="true"></span>
        <strong>${title}</strong>
        <span>${detail}</span>
      </div>`;
    },
    exportEnabled() {
      return this.sqlViewConfig?.exportEnabled !== false;
    },

    /** localStorage key scoped to this grid's table */
    columnStateKey() {
      const table = this.sqlViewConfig?.tableName;
      if (!table) return null;
      // The Structured Register has deliberate content-led widths. Keep its persisted
      // user adjustments separate from legacy flex-column state so an old automatic
      // width snapshot cannot collapse the new visual treatment on first render.
      return this.dashboardStyle
        ? `sqlgrid_colstate_structured_v1_${table}`
        : `sqlgrid_colstate_${table}`;
    },

    // Saved config filters + grid runtime filters + widget-menu filters + the TRANSIENT same-table
    // cross-filter predicates. The selection predicates come last and are merged here (payload
    // build time) rather than written into any filter state, so clearing a chart selection simply
    // stops contributing them. Shared by the datasource, export, and Isolate-All so the isolated
    // set is always exactly the rows the grid is showing.
    mergedFilters() {
      const configFilters = this.sqlViewConfig?.filters || [];
      const activeRuntime = this.runtimeFilters.filter(f => f.column);
      const widgetLevelFilters = this.widgetFilters?.filters || [];
      return [...configFilters, ...activeRuntime, ...widgetLevelFilters, ...this.selectionFilters];
    },

    /** The explicit row window this grid displays (`RowWindow` contract): a runtime Top/Bottom
     *  wins over the saved one, otherwise every matching row. */
    rowWindow() {
      return deriveGridRowWindow(this.widgetFilters, this.sqlViewConfig);
    },

    /**
     * The isolate action's label.
     *
     * It must NOT claim "all matching". The action now sends this grid's own `rowWindow`, so on a
     * Top-10 grid it isolates exactly those ten — which is the point: a visible Top-10 grid that
     * isolated all thirteen is the defect this feature removes (spec, "Why the current paths are
     * not sufficient"), and invariant 2 requires the visible rows, the occurrences and the legend
     * records to share one exact scope. A label promising the whole match would be the same scope
     * lie moved from the resolver into the button.
     *
     * How far the action reached is stated AFTER it runs, by the receipt: the legend renders
     * `10 of 13 filtered` from the server's own `recordsInWindow`/`totalFilteredBeforeWindow`. It
     * cannot be stated before, because only a resolver receipt knows the pre-window total —
     * `/tables/data` counts `COUNT(*) OVER()` the ALREADY-WINDOWED scope, so its `totalCount` on a
     * top-10 grid is 10, not 13.
     */
    isolateAllLabel() {
      return 'Isolate All';
    },

    /** Same label for the compact toolbar button. */
    isolateFilteredLabel() {
      return 'Isolate filtered';
    },

    link3D() {
      return !!this.sqlViewConfig?.link3D;
    },

    rowSpatialActionsAvailable() {
      return this.link3D
        && this.sourceSpatialStatus === 'available'
        && this.loadedSpatialEligibility !== 'none';
    },

    bulkSpatialAvailable() {
      return this.rowSpatialActionsAvailable
        && this.loadedSpatialEligibility !== 'relatedOnly';
    },

    spatialSourceKey() {
      return `${String(this.sqlViewConfig?.tableName || '').toLowerCase()}|${this.link3D ? 'linked' : 'unlinked'}`;
    },

    /** Column whose values drive 3D element colour on isolation (null when unconfigured). */
    coloringColumn() {
      return this.sqlViewConfig?.coloring?.column || null;
    },

    /** value(string) → hex map assigned in the widget editor. */
    valueColors() {
      return this.sqlViewConfig?.coloring?.valueColors || {};
    },

    /** True when Link-3D is on AND a colouring column is configured. Gates both the paint
     *  on single-row isolate and the "Isolate All" button (the colouring column is the
     *  low-cardinality partition the all-rows resolution iterates over). */
    hasColoring() {
      return !!(this.link3D && this.coloringColumn);
    },

    /** Resolve the server-owned descriptor, falling back only for widgets saved before it existed. */
    resolvedTagLink() {
      if (!this.link3D) return null;
      const keys = this.allColumnNames.length
        ? this.allColumnNames
        : (this.message?.content?.length
          ? Object.keys(this.message.content[0])
          : (this.sqlViewConfig?.columns || []));
      const serverTagLink = this.sqlViewConfig
        && Object.prototype.hasOwnProperty.call(this.sqlViewConfig, 'tagLink')
        ? this.sqlViewConfig.tagLink
        : undefined;
      return resolveTagLink({
        serverTagLink,
        legacyTagColumn: this.sqlViewConfig?.tagColumn,
        tableName: this.sqlViewConfig?.tableName,
        availableColumns: keys,
      });
    },

    resolvedTagColumn() {
      return this.resolvedTagLink?.column || null;
    },

    canIsolateSelectedRow() {
      return !!(this.link3D && this.resolvedTagLink && this.canIsolateRow(this.selectedRowData));
    },

    resolvedDefaultColDef() {
      if (!this.dashboardStyle) return this.defaultColDef;
      return {
        ...this.defaultColDef,
        flex: undefined,
        width: 151,
        minWidth: 116,
        suppressMenu: true,
        unSortIcon: true,
      };
    },

    scopeChips() {
      const chips = [];
      if (this.searchText) {
        chips.push({ key: SCOPE_CHIP.SEARCH, label: `Search “${this.searchText}”`, removable: true });
      }
      if (this.activeFilterCount > 0) {
        const noun = this.activeFilterCount === 1 ? 'widget filter' : 'widget filters';
        chips.push({ key: SCOPE_CHIP.FILTERS, label: `${this.activeFilterCount} ${noun}`, removable: true });
      }
      for (const [index, filter] of (this.sqlViewConfig?.filters || []).entries()) {
        chips.push({
          key: `saved-filter-${index}`,
          label: filterConditionLabel(filter, this.getColumnDisplayName(filter.column)),
          removable: false,
          hint: 'Included in this chart or table by its author.',
        });
      }

      // Only a grid header sort is locally owned (removable here). A sort coming from the widget
      // filter menu is owned there, so it is listed with a hint rather than a remove control.
      const isGridSort = !!this.sortColumn;
      const sortColumn = this.sortColumn || this.widgetFilters?.sort?.column;
      const sortDirection = this.sortColumn
        ? this.sortDirection
        : (this.widgetFilters?.sort?.direction || 'asc');
      if (sortColumn) {
        chips.push({
          key: SCOPE_CHIP.SORT,
          label: `Sort ${this.getColumnDisplayName(sortColumn)} · ${sortDirection === 'desc' ? 'descending' : 'ascending'}`,
          removable: isGridSort,
          hint: isGridSort ? undefined : 'Set in the widget filters.',
        });
      }

      if (this.filterSessionId || (this.globalFilters && Object.keys(this.globalFilters).length)) {
        chips.push({
          key: 'dashboard',
          label: 'Dashboard filters active',
          removable: false,
          hint: 'Managed in the dashboard Filters panel.',
        });
      }
      return chips;
    },

    hasClearableScope() {
      return !!this.searchText || this.activeFilterCount > 0;
    },

    /** Accessible label for the queries menu activator, including the applied-query count. */
    queriesButtonLabel() {
      const n = this.scopeChips.length;
      return n ? `Filters applied (${n})` : 'Filters applied (none)';
    },

    columnDefs() {
      const defs = [];

      // Add 3D viewer click column if link3D is enabled and a tag column is found
      if (this.rowSpatialActionsAvailable && this.resolvedTagColumn) {
        defs.push({
          headerName: '3D',
          field: '__viewer',
          width: this.dashboardStyle ? 61 : 60,
          minWidth: this.dashboardStyle ? 61 : undefined,
          maxWidth: this.dashboardStyle ? 61 : 60,
          pinned: 'left',
          sortable: false,
          filter: false,
          resizable: false,
          suppressMovable: true,
          lockPosition: 'left',
          headerClass: this.dashboardStyle ? 'sql-grid-viewer-header' : undefined,
          cellClass: this.dashboardStyle ? 'sql-grid-viewer-cell' : undefined,
          cellRenderer: 'ViewerClickCellRenderer',
          cellRendererParams: {
            tagColumn: this.resolvedTagColumn,
            clicked: this.onViewerClick,
            canIsolate: this.canIsolateRow,
            icon: this.dashboardStyle ? 'mdi-cube-outline' : undefined,
          },
        });
      }

      // Auto-generate columns from content data
      if (this.message?.content && this.message.content.length > 0) {
        const sampleRow = this.message.content[0];
        const columnTypes = this.sqlViewConfig?.columnTypes || {};
        const savedState = this.loadColumnState();
        const configuredColumns = Array.isArray(this.sqlViewConfig?.columns)
          ? this.sqlViewConfig.columns
          : [];
        const hasAutoAppendedLinkColumn = !!(
          this.resolvedTagColumn
          && configuredColumns.length > 0
          && !configuredColumns.some(
            column => String(column).toLowerCase() === this.resolvedTagColumn.toLowerCase(),
          )
        );

        // Build keys, applying saved order when available
        const keys = Object.keys(sampleRow)
          .filter(k => (
            k !== '__viewer'
            && !ROW_SPATIAL_METADATA.includes(k)
            && k.toLowerCase() !== 'tagids'
            && !(
              hasAutoAppendedLinkColumn
              && k.toLowerCase() === this.resolvedTagColumn.toLowerCase()
            )
          ));
        if (savedState) {
          const orderMap = {};
          savedState.forEach((entry, idx) => { orderMap[entry.colId] = idx; });
          keys.sort((a, b) => (orderMap[a] ?? Infinity) - (orderMap[b] ?? Infinity));
        }

        keys.forEach(key => {
          const savedCol = savedState?.find(s => s.colId === key);
          const col = {
            headerName: this.dashboardStyle
              ? this.formatDashboardHeaderName(this.getColumnDisplayName(key))
              : this.getColumnDisplayName(key),
            field: key,
            cellClass: this.dashboardStyle ? 'sql-grid-data-cell' : undefined,
            cellRenderer: this.dashboardStyle
              ? params => this.createDataCell(params.valueFormatted ?? params.value)
              : undefined,
            tooltipValueGetter: this.dashboardStyle
              ? params => {
                  const value = params.valueFormatted ?? params.value;
                  return value === null || value === undefined ? '' : String(value);
                }
              : undefined,
          };

          if (this.dashboardStyle) {
            Object.assign(col, this.getDashboardColumnLayout(key));
          }

          const colorRule = columnColorRule(this.sqlViewConfig?.columnColoring, key);
          if (colorRule) {
            col.cellRenderer = params => this.createStatusPill(params.valueFormatted ?? params.value, colorRule);
          } else if (this.dashboardStyle && this.isStatusColumn(key)) {
            col.cellRenderer = params => this.createStatusPill(params.value);
          }

          // Apply saved width so columns don't reset to flex on recompute
          if (savedCol?.width) {
            col.width = savedCol.width;
            col.flex = undefined;
          }

          // Format based on data type
          const dataType = columnTypes[key];
          if (dataType === 'decimal' || dataType === 'float' || dataType === 'numeric' || dataType === 'money') {
            if (this.dashboardStyle) col.cellClass = ['sql-grid-data-cell', 'sql-grid-number-cell'];
            col.valueFormatter = params => {
              if (params.value === null || params.value === undefined) return '';
              return Number(params.value).toFixed(2);
            };
          } else if (DATE_SQL_TYPES.includes(dataType) && dataType !== 'time') {
            if (this.dashboardStyle) col.cellClass = 'sql-grid-data-cell';
            col.valueFormatter = params => {
              if (!params.value) return '';
              return new Date(params.value).toLocaleDateString();
            };
          }

          defs.push(col);
        });
      }

      return defs;
    },
  },

  created() {
    // Per-instance debounced search. Declared here (not in `methods`) so lodash's `.cancel`
    // survives: Vue's initMethods binds every `methods` entry, and Function.prototype.bind drops
    // a debounced function's own `.cancel`/`.flush` props (verified on Vue 2.7.15 —
    // `this.onSearchChange.cancel` is undefined and throws). The methods-declared version made the
    // clear paths below throw before they could reset `searchText`, so the input/chip kept the stale
    // term. A per-instance debounce also stops sibling grids on the same board sharing one timer.
    this.onSearchChange = debounce(() => {
      if (this.gridApi) {
        this.setDatasource();
      }
      this.$nextTick(() => this.calcGridHeight());
    }, 400);

    // Memoises the catalogue capability read (one `tables()` GET per widget instance). An instance
    // property, not `data`: nothing renders from the promise itself.
    this._spatialPromise = null;
    // Viewer-session policy and replay intent are deliberately non-reactive and never enter the
    // widget config. The parent supplies the current choice immediately before each grid isolate.
    this._spatialFocusSessionOverride = null;
    this._onSpatialFocusFallbackResolved = null;
    this._lastTypedSpatialFocus = null;
    this._filteredSpatialController = null;
    // The ONE spatial seam. The composable owns the per-viewer generation, so this is a façade.
    this.focus = useSpatialFocus();
    this.loadSourceSpatialCapability();
  },

  mounted() {
    this.$nextTick(() => {
      this.calcGridHeight();
      this.resizeObserver = new ResizeObserver(() => this.calcGridHeight());
      if (this.$refs.gridWrapper) {
        this.resizeObserver.observe(this.$refs.gridWrapper);
      }
    });
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.onSearchChange.cancel();
    this.invalidateFilteredSpatial();
  },

  methods: {
    captureIsolationClaim() {
      const claim = this.isolationClaim;
      if (!claim) return null;
      return {
        generation: claim.generation,
        ownerWidgetId: claim.ownerWidgetId,
      };
    },

    canWriteIsolation(claim) {
      return !claim || this.isClaimCurrent(claim) !== false;
    },

    calcGridHeight() {
      // Structured (dashboard) grids fill their widget body via flex — the stack grows inside the
      // flex column (.sql-view-ag-grid-stack--structured), so no JS height write is needed here. An
      // inline px height would fight the flex-fill and reintroduce the card-bleed. Only the compact/
      // mini consumers size the grid from the measured wrapper height below.
      if (this.dashboardStyle) return;
      const wrapper = this.$refs.gridWrapper;
      if (!wrapper) return;
      const wrapperHeight = wrapper.clientHeight;
      const toolbar = wrapper.querySelector('.sql-view-data-grid-toolbar');
      const toolbarMargin = toolbar ? parseFloat(window.getComputedStyle(toolbar).marginBottom) || 0 : 0;
      const toolbarHeight = toolbar ? toolbar.offsetHeight + toolbarMargin : 0;
      this.gridHeight = Math.max(wrapperHeight - toolbarHeight, 200);
    },

    isStatusColumn(key) {
      return /(status|severity|risk|susceptibility)$/i.test(String(key));
    },

    getDashboardColumnLayout(key) {
      const label = String(key);
      if (this.isStatusColumn(label)) return { width: 132, minWidth: 124 };
      if (/(date|due|raised|inspection)/i.test(label)) return { width: 149, minWidth: 134 };
      if (/(description|finding|recommend|comment|remark|action)/i.test(label)) {
        return { width: 242, minWidth: 200 };
      }
      if (/(line|equipment|source|reference|iso|cml|tag)/i.test(label)) {
        return { width: 168, minWidth: 137 };
      }
      if (/(id|number|no)$/i.test(label)) return { width: 132, minWidth: 116 };
      return { width: 151, minWidth: 124 };
    },

    formatDashboardHeaderName(label) {
      if (!label || label !== label.toUpperCase()) return label;
      const acronyms = new Set(['3D', 'CML', 'ID', 'ISO', 'NO']);
      return label.split(/(\s+|\/|-)/).map(part => {
        if (!part.trim() || part === '/' || part === '-') return part;
        if (acronyms.has(part)) return part;
        return part.charAt(0) + part.slice(1).toLowerCase();
      }).join('');
    },

    createStatusPill(value, colorRule = null) {
      if (value === null || value === undefined || value === '') return '';
      const text = String(value);
      const normalized = text.toLowerCase();
      let tone = 'neutral';
      if (colorRule) tone = 'neutral';
      else if (/(critical|high|open|overdue|failed|error)/.test(normalized)) tone = 'high';
      else if (/(medium|review|pending|warning)/.test(normalized)) tone = 'medium';
      else if (/(low|closed|complete|completed|success|resolved)/.test(normalized)) tone = 'low';
      else if (/(monitoring|active|info|in progress)/.test(normalized)) tone = 'info';

      const pill = document.createElement('span');
      pill.className = `sql-grid-status-pill sql-grid-status-pill--${tone}`;
      if (colorRule) Object.assign(pill.style, columnStatusStyle(colorRule, value));
      const label = document.createElement('span');
      label.className = 'sql-grid-status-pill__label';
      label.textContent = text;
      pill.appendChild(label);
      return pill;
    },

    createDataCell(value) {
      const text = document.createElement('span');
      text.className = 'sql-grid-cell-text';
      text.textContent = value === null || value === undefined ? '' : String(value);
      return text;
    },

    clearLocalScope() {
      const hadSearch = !!this.searchText;
      this.onSearchChange.cancel();
      this.searchText = '';
      this.invalidateFilteredSpatial();
      if (this.activeFilterCount > 0) this.$emit('clearFilters');
      else if (hadSearch && this.gridApi) this.setDatasource();
    },

    /** Remove a single applied query from the queries menu. Only locally-owned scope is
     *  removable (search, widget filters as a group, grid header sort); saved/dashboard scope
     *  is listed but owned elsewhere. Routes through the existing clear pathways/emits — no
     *  parallel filter engine here. */
    removeScopeChip(chip) {
      if (!chip || !chip.removable) return;
      switch (chip.key) {
        case SCOPE_CHIP.SEARCH:
          log('SqlViewDataGrid', 'queries menu: remove search');
          this.onSearchChange.cancel();
          this.searchText = '';
          this.invalidateFilteredSpatial();
          if (this.gridApi) this.setDatasource();
          break;
        case SCOPE_CHIP.FILTERS:
          log('SqlViewDataGrid', 'queries menu: remove widget filters');
          this.$emit('clearFilters');
          this.invalidateFilteredSpatial();
          break;
        case SCOPE_CHIP.SORT:
          log('SqlViewDataGrid', 'queries menu: remove grid sort');
          this.clearGridSort();
          break;
      }
    },

    /** Reset the grid's interactive (header) sort. Any baked widget default sort reasserts as
     *  the default on the next fetch (datasource sort precedence handles the fallback). */
    clearGridSort() {
      this.sortColumn = null;
          this.sortDirection = 'asc';
          this.invalidateFilteredSpatial();
      if (this.columnApi) {
        this.columnApi.applyColumnState({ defaultState: { sort: null } });
      }
      if (this.gridApi) this.setDatasource();
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      this.initColumns();
      this.setDatasource();
      this.$nextTick(() => this.restoreColumnState());
    },

    initColumns() {
      let newColumns;
      if (this.message?.content && this.message.content.length > 0) {
        newColumns = Object.keys(this.message.content[0]);
      } else if (this.sqlViewConfig?.columns?.length && this.allColumnNames.length === 0) {
        // Fallback: use config columns when no data has arrived yet
        newColumns = [...this.sqlViewConfig.columns];
      }

      if (newColumns) {
        this.allColumnNames = newColumns;
      }
    },

    setDatasource() {
      if (!this.gridApi || !this.sqlViewConfig) return;

      const datasource = {
        getRows: (params) => {
          // Empty-match (decision 10): this widget's filter session was created with 0 tags, so
          // the combined chart selection matches nothing. Return an empty page WITHOUT calling
          // the api — the api would return a full unfiltered table alongside 'session-expired',
          // and rendering that here is exactly the misleading board the decision forbids.
          if (this.sessionEmptyMatch) {
            params.successCallback([], 0);
            this.gridHasSettled = true;
            this.gridLoading = false;
            this.gridSoftRefresh = false;
            return;
          }
          this.gridLoading = true;
          this._gridRequestSeq = (this._gridRequestSeq || 0) + 1;
          const requestSeq = this._gridRequestSeq;
          const skip = params.startRow;
          const take = params.endRow - params.startRow;

          // Sort precedence: an explicit grid column-header sort wins (ad-hoc view re-sort), then a
          // runtime widget-filter sort, then the widget's persisted default sort (A2 — a baked default
          // a reload restores). /tables/data is client-composed and honors whatever the request
          // carries, so runtime genuinely overrides the default; most specific wins.
          let effectiveSortColumn = this.sortColumn;
          let effectiveSortDirection = this.sortDirection;
          if (!effectiveSortColumn && this.widgetFilters?.sort?.column) {
            effectiveSortColumn = this.widgetFilters.sort.column;
            effectiveSortDirection = this.widgetFilters.sort.direction || 'asc';
          }
          if (!effectiveSortColumn && this.sqlViewConfig?.sort?.column) {
            effectiveSortColumn = this.sqlViewConfig.sort.column;
            effectiveSortDirection = this.sqlViewConfig.sort.direction || 'asc';
          }

          const payload = {
            tableName: this.sqlViewConfig.tableName,
            // Row isolation needs the physical link value after the page-level availability check.
            // Request it once with the page even when the author hid it from visible grid columns;
            // columnDefs stays driven by the configured presentation columns, so it remains hidden.
            columns: [
              ...(this.sqlViewConfig.columns || []),
              ...(
                this.resolvedTagColumn
                && !(this.sqlViewConfig.columns || []).some(
                  column => String(column).toLowerCase() === this.resolvedTagColumn.toLowerCase(),
                )
                  ? [this.resolvedTagColumn]
                  : []
              ),
            ],
            filters: this.mergedFilters,
            facilityId: this.$route.params.id,
            includeIsolationAvailability: this.sourceSpatialStatus === 'available' && !!this.resolvedTagLink,
            skip,
            take,
            sortColumn: effectiveSortColumn,
            sortDirection: effectiveSortDirection,
          };

          // Top/Bottom: limit total result set. A runtime widget-filter cap wins; else the widget's
          // persisted default row cap (A2 action list — e.g. bottom 10 by remaining life; a baked
          // default a reload restores, overridable at runtime).
          if (this.widgetFilters?.topBottom?.mode) {
            payload.topBottom = this.widgetFilters.topBottom;
          } else if (this.sqlViewConfig?.topBottom?.mode) {
            payload.topBottom = this.sqlViewConfig.topBottom;
          }

          this.applyActiveFilters(payload);

          apiService.tableData(payload)
            .then(res => {
              const { rows, totalCount } = res.data;
              if (this.configurationRequired) {
                // Recovered (mode was set): drop the manually shown not-configured overlay.
                this.configurationRequired = false;
                if (this.gridApi) this.gridApi.hideOverlay();
              }
              params.successCallback(rows, totalCount);
              this.observeLoadedSpatialEligibility(rows, totalCount, skip);
              this.gridHasSettled = true;
            })
            .catch(err => {
              // Not a failure of the query or of a selection predicate: the register cannot be
              // read until an administrator sets its mode. Resolve the block as zero rows and show
              // the no-rows overlay with the calm copy. The flag is set first because ag-grid-vue
              // applies the template prop on the next tick, and the overlay is shown explicitly:
              // the infinite row model's isEmpty() is "no cache", so AG never auto-shows it on
              // zero rows. Must NOT reach reportSelectionFilterRejection, which reads every 400
              // under a live selection as a rejected predicate and drops the scene.
              if (isRegisterConfigurationRequiredError(err)) {
                log('SqlViewDataGrid', 'source register needs an assignment mode', this.sqlViewConfig?.tableName);
                this.configurationRequired = true;
                this.$nextTick(() => {
                  if (!this.gridApi) return; // tile torn down while the request was in flight
                  params.successCallback([], 0);
                  this.gridApi.showNoRowsOverlay();
                });
                return;
              }
              error('SqlViewDataGrid', 'datasource error', err);
              this.reportSelectionFilterRejection(err);
              params.failCallback();
            })
            .finally(() => {
              if (requestSeq === this._gridRequestSeq) {
                this.gridLoading = false;
                this.gridSoftRefresh = false;
              }
            });
        },
      };

      this.gridApi.setDatasource(datasource);
    },

    /**
     * Re-query the datasource without dropping what's on screen. Once the grid has settled a
     * first result, refreshInfiniteCache() re-runs getRows while AG retains the displayed rows,
     * and the template overlays the scrim + progress strip (refresh-with-data presentation).
     * Before the first settle there is nothing to retain, so fall back to the full datasource
     * reset whose rows skeleton is the correct first-load presentation. The datasource closures
     * read live component state, so the re-run naturally picks up the new session/filters.
     */
    refreshDatasourceSoft() {
      if (!this.gridApi) return;
      if (this.gridHasSettled) {
        this.gridSoftRefresh = true;
        this.gridApi.refreshInfiniteCache();
      } else {
        this.setDatasource();
      }
    },

    /**
     * Coalesce same-tick refresh triggers into one soft refresh. A selection commit flips
     * filterSessionId and sessionEmptyMatch together (and degraded transitions can bundle
     * globalFilters), each with its own watcher; without batching, one interaction re-ran
     * the whole infinite cache once per watcher.
     */
    scheduleSoftRefresh() {
      // A frozen grid keeps the rows it already has: its table lost its external-only session, so
      // a refetch would silently widen it through the full session's tag intersection.
      if (this.frozen && this.gridHasSettled) return;
      if (this._softRefreshQueued) return;
      this._softRefreshQueued = true;
      this.$nextTick(() => {
        this._softRefreshQueued = false;
        this.refreshDatasourceSoft();
      });
    },

    /**
     * Applies the active cross-filter (server-side session, or global + legend when no session)
     * and the search text onto a request payload. Shared by the grid datasource and "Isolate All"
     * so the isolated tag set always matches exactly the rows the grid is showing.
     * @param {object} payload - mutated in place
     * @returns {object} the same payload
     */
    /**
     * Fail-closed reporting for a rejected same-table selection predicate.
     *
     * A chart selection can name a column this widget's table no longer admits. The api answers
     * 400 and this grid keeps that outcome: it never refetches without the predicate, because a
     * retry without it would quietly show the UNFILTERED table while the board still displays the
     * selection chip. Raise it instead so the dashboard can name the failure and drop the scene.
     *
     * Only a 400 raised while transient selection predicates were actually in the payload counts —
     * an ordinary bad-request from the widget's own saved filters is not a selection failure.
     */
    reportSelectionFilterRejection(err) {
      if (!this.selectionFilters.length) return;
      if (err?.response?.status !== 400) return;
      const admitted = new Set(
        (this.allColumnNames.length ? this.allColumnNames : (this.sqlViewConfig?.columns || []))
          .map(column => String(column).toLowerCase()),
      );
      const rejected = this.selectionFilters
        .find(filter => filter && filter.column && !admitted.has(String(filter.column).toLowerCase()))
        || this.selectionFilters[0];
      this.$emit('selectionFilterRejected', {
        column: rejected?.column || null,
        tableName: this.sqlViewConfig?.tableName || null,
      });
    },

    applyActiveFilters(payload) {
      // Prefer filterSessionId (server-side cached filter set) over raw filters
      if (this.filterSessionId) {
        payload.filterSessionId = this.filterSessionId;
      } else {
        if (this.globalFilters && Object.keys(this.globalFilters).length) {
          payload.globalFilters = this.globalFilters;
        }
      }

      if (this.searchText) {
        payload.search = this.searchText;
      }

      return payload;
    },

    onGridSearchInput() {
      this.invalidateFilteredSpatial();
      this.onSearchChange();
    },

    onSortChanged() {
      if (!this.columnApi) return;
      // AG Grid v30 removed gridApi.getSortModel(); rebuild the same [{ colId, sort }]
      // shape from column state so the server-side datasource keeps sorting correctly.
      const sortModel = this.columnApi.getColumnState()
        .filter(col => col.sort)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map(col => ({ colId: col.colId, sort: col.sort }));
      if (sortModel && sortModel.length > 0) {
        this.sortColumn = sortModel[0].colId;
        this.sortDirection = sortModel[0].sort;
      } else {
        this.sortColumn = null;
        this.sortDirection = 'asc';
      }
      this.invalidateFilteredSpatial();
    },

    /** Save column widths and order to localStorage via AG Grid Column State API */
    saveColumnState: debounce(function () {
      if (!this.columnApi || !this.columnStateKey) return;
      try {
        const state = this.columnApi.getColumnState();
        localStorage.setItem(this.columnStateKey, JSON.stringify(state));
      } catch (err) {
        // localStorage full or unavailable — silently ignore
      }
    }, 500),

    /** Load saved column state from localStorage */
    loadColumnState() {
      if (!this.columnStateKey) return null;
      try {
        const raw = localStorage.getItem(this.columnStateKey);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        return null;
      }
    },

    /** Restore saved column state (widths + order) after grid is ready */
    restoreColumnState() {
      const saved = this.loadColumnState();
      if (!saved || !this.columnApi) return;
      this.columnApi.applyColumnState({ state: saved, applyOrder: true });
    },

    onColumnResized(event) {
      // AG Grid also emits resize events for flex/layout calculations. Persist only a
      // deliberate drag so the showcase widths remain the default until the user edits them.
      if (event.finished && event.source === 'uiColumnResized') {
        this.saveColumnState();
      }
    },

    onColumnMoved() {
      this.saveColumnState();
    },

    getColumnDisplayName(key) {
      const displayNames = this.sqlViewConfig?.columnDisplayNames || {};
      return displayNames[key] || this.formatHeaderName(key);
    },

    formatHeaderName(key) {
      return formatHeaderName(key);
    },

    async exportData() {
      if (!this.sqlViewConfig) return;
      // Empty-match: the active chart selection matches zero rows, and a 0-tag session reads as
      // expired server-side (the export endpoint fails closed on it and would error out).
      // Nothing to export - mirror isolateAllFilteredRows' short-circuit instead.
      if (this.sessionEmptyMatch) {
        this.$dialog?.notify?.warning('No rows match the current chart selection.', { position: 'top-right', timeout: 3000 });
        return;
      }

      this.exporting = true;
      try {
        const payload = {
          tableName: this.sqlViewConfig.tableName,
          columns: this.sqlViewConfig.columns,
          filters: this.mergedFilters,
          facilityId: this.$route.params.id,
        };
        if (this.filterSessionId) {
          payload.filterSessionId = this.filterSessionId;
        } else {
          if (this.globalFilters && Object.keys(this.globalFilters).length) {
            payload.globalFilters = this.globalFilters;
          }
        }
        const res = await apiService.tableExport(payload);
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.sqlViewConfig.tableName}_export.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        error('SqlViewDataGrid', 'export error', err);
        this.$dialog?.notify?.error('Export failed. Please try again.', { position: 'top-right', timeout: 3000 });
      } finally {
        this.exporting = false;
      }
    },

    onViewerClick(rowData) {
      if (!this.canIsolateRow(rowData)) return;
      this.suppressRowDetailsUntil = Date.now() + VIEWER_INTERACTION_SUPPRESSION_MS;
      if (!this.dashboardStyle) this.gridApi?.deselectAll();
      this.selectedRowData = rowData;
      this.$store.commit('SET_VIEWER_TYPE', '3d');
      this.$emit('openViewer', this.nextIsolationOperation('row'));
    },

    onRowClicked(event) {
      if (!event?.data) return;
      event.node.setSelected(true);
      this.selectedRowData = event.data;
    },

    onRowDoubleClicked(event) {
      if (!event?.data || this.isViewerActionEvent(event)) return;
      this.selectedRowData = event.data;
      this.$emit('openRowDetails', event.data);
    },

    onCellKeyDown(event) {
      if (
        event?.event?.key !== 'Enter'
        || !event.data
        || this.isViewerActionEvent(event)
      ) return;
      event.event.preventDefault?.();
      this.selectedRowData = event.data;
      this.$emit('openRowDetails', event.data);
    },

    isViewerActionEvent(event) {
      const target = event?.event?.target;
      return Date.now() < this.suppressRowDetailsUntil
        || event?.colDef?.field === '__viewer'
        || event?.column?.colId === '__viewer'
        || event?.column?.getColId?.() === '__viewer'
        || !!target?.closest?.('.viewer-click-cell-renderer__button')
        || !!target?.closest?.('[col-id="__viewer"]');
    },

    /** Toolbar action: open the 3D viewer and isolate the currently selected row's
     *  tag(s). Reuses the existing openViewer → isolateSelectedTag path, so registers
     *  resolve via the plural resolveTagsByFilter (multi-tag safe). */
    isolateSelectedRow() {
      if (!this.canIsolateRow(this.selectedRowData)) return;
      this.$store.commit('SET_VIEWER_TYPE', '3d');
      this.$emit('openViewer', this.nextIsolationOperation('row'));
    },

    canIsolateRow(rowData) {
      return rowCanIsolate(rowData, this.resolvedTagLink);
    },

    /** Called by parent after 3D viewer is loaded — resolves the tag(s) via API then isolates in the model */
    async isolateSelectedTag(operation = null) {
      if (operation && operation.token !== this.isolationIntentSeq) return;
      if (operation?.kind === 'filteredSpatial') {
        return this.showPreparedFilteredSpatial(operation, this.captureIsolationClaim());
      }
      // "Isolate All" rides this same viewer-open → isolateSelectedTag handshake; branch here
      // so the post-load callback runs the all-rows path for the widget that requested it.
      // Only the widget whose button was clicked has the flag set — siblings fall through and
      // no-op on the selectedRowData guard below.
      if (operation?.kind === 'isolateAll') {
        if (this.pendingIsolateAllToken !== operation.token) return;
        this.cancelPendingIsolation(operation);
        return this.isolateAllFilteredRows(this.captureIsolationClaim());
      }
      if (this.pendingIsolateAll && !operation) {
        this.pendingIsolateAll = false;
        this.pendingIsolateAllToken = null;
        return this.isolateAllFilteredRows(this.captureIsolationClaim());
      }

      const viewer = this.$store.state.viewer;
      if (!viewer) return;

      const tagLink = this.resolvedTagLink;
      if (!this.link3D || !this.selectedRowData || !tagLink) return;
      const isolationClaim = this.captureIsolationClaim();

      const tree = viewer.model?.getInstanceTree();
      if (!tree) {
        this.$dialog?.notify?.warning('Model not loaded yet.', {
          position: 'top-right',
          timeout: 3000,
        });
        return;
      }

      // The exact row path. It is taken when the source proves a capability AND the loaded row
      // carries `spatial.rowKey` — the server projects that key as a machine column for exactly this
      // purpose (`SqlViewQueryService.WithSpatialRowKeyColumn`), so a capable source's rows can name
      // themselves. Sending a GUESSED key would be answered `unknownRowKey`, so a row that does not
      // carry the key is never resolved client-side.
      //
      // What it must never be is SILENT. A capable source whose row arrived without its key means
      // the page was served by an API build that predates the machine column (or by a route that
      // does not project it) — a real shortfall, not a design decision. Say so, name the column,
      // clear the previous scene and stop. Reinterpreting TagId here would create a second render
      // authority for the same gesture.
      const spatial = await this.ensureSpatialCapability();
      if (!this.canWriteIsolation(isolationClaim)) return;
      if (hasExactSpatialCapability(spatial)) {
        const scope = buildSpatialRowScope({
          tableName: this.sqlViewConfig.tableName,
          rowKeys: [spatialRowKeyValue(this.selectedRowData, spatial)],
          expectedSourceRevision: spatial.sourceRevision,
          encodingColumn: this.hasColoring ? this.coloringColumn : null,
        });
        if (scope) return this.showExactSpatialFocus(scope, spatial, isolationClaim, this.selectedRowData.__SpatialActionMode || 'source');
        this.reportExactPathShortfall('row', `the row is missing its "${spatial.rowKey}" key`, {
          tableName: this.sqlViewConfig.tableName,
          rowKey: spatial.rowKey,
        });
        return this.refuseSpatialFocus(
          'spatialRowKeyMissing',
          `This row cannot be shown exactly because it is missing its "${spatial.rowKey}" spatial key. Refresh the dashboard and try again.`,
          isolationClaim,
        );
      } else {
        return this.refuseSpatialFocus(
          'spatialCapabilityMissing',
          'This data source has no published spatial target or context. Configure its Spatial Focus roles and republish it.',
          isolationClaim,
        );
      }

    },

    getExplanationScope() {
      if (this.frozen) return null;
      const runtimeFilters = this.runtimeFilters.filter(filter => filter?.column);
      let sort = null;
      if (this.sortColumn) {
        sort = { column: this.sortColumn, direction: this.sortDirection || 'asc' };
      } else if (this.widgetFilters?.sort?.column) {
        sort = { ...this.widgetFilters.sort };
      }
      return {
        columns: [...(this.sqlViewConfig?.columns || [])],
        sourceSearch: this.searchText || null,
        runtimeFilters,
        sort,
        topBottom: this.widgetFilters?.topBottom?.mode ? { ...this.widgetFilters.topBottom } : null,
        rowWindow: this.rowWindow,
      };
    },

    observeLoadedSpatialEligibility(rows, totalCount, skip) {
      if (this.sourceSpatialStatus !== 'available' || !Array.isArray(rows)) return;
      const previous = this.loadedSpatialEligibility;
      const hasOwnLocation = rows.some(row => row?.__CanIsolate === true
        && ['ownElement', 'ownPoint'].includes(row.__SpatialAvailability)
        && row.__SpatialActionMode === 'source');
      const hasRelatedAction = rows.some(row => row?.__CanIsolate === true
        && row.__SpatialAvailability === 'relatedEquipment'
        && row.__SpatialActionMode === 'relatedEquipment');
      const completeKnownSet = skip === 0
        && totalCount > 0
        && rows.length === totalCount
        && rows.every(row => typeof row?.__CanIsolate === 'boolean'
          && !!row.__SpatialAvailability
          && row.__SpatialAvailability !== 'unknown');
      if (hasOwnLocation) {
        this.loadedSpatialEligibility = 'source';
        this.setSourceSpatialStatus('available');
      } else if (completeKnownSet) {
        this.loadedSpatialEligibility = hasRelatedAction ? 'relatedOnly' : 'none';
        this.$emit('spatialCapability', { status: 'unavailable', reason: 'noLocations' });
      } else {
        this.loadedSpatialEligibility = 'unknown';
        this.setSourceSpatialStatus('available');
      }
      if (previous !== this.loadedSpatialEligibility) this.initColumns();
    },

    buildFilteredSpatialScope(spatial) {
      if (this.frozen || this.sessionEmptyMatch || !spatial?.sourceRevision) return null;
      const globalOnly = !this.filterSessionId
        && !!(this.globalFilters && Object.keys(this.globalFilters).length);
      if (globalOnly) return null;
      return buildSpatialQueryScope({
        tableName: this.sqlViewConfig?.tableName,
        columns: this.sqlViewConfig?.columns,
        filters: this.mergedFilters,
        search: this.searchText,
        filterSessionId: this.filterSessionId,
        expectedSourceRevision: spatial.sourceRevision,
        encodingColumn: this.hasColoring ? this.coloringColumn : null,
        rowWindow: this.rowWindow,
      });
    },

    filteredSpatialFingerprint(scope) {
      return scope ? JSON.stringify(scope) : '';
    },

    async isolateFilteredRows() {
      if (this._filteredSpatialController) this._filteredSpatialController.abort();
      this._filteredSpatialController = new AbortController();
      this.filteredSpatialSeq += 1;
      const sequence = this.filteredSpatialSeq;
      const signal = this._filteredSpatialController.signal;
      const operation = this.nextIsolationOperation('filteredSpatial');
      const claimed = this.claimIsolation?.(this.focusOwner().widgetId, operation);
      const isolationClaim = claimed
        ? { generation: claimed.generation, ownerWidgetId: claimed.ownerWidgetId }
        : this.captureIsolationClaim();
      if (isolationClaim) operation.isolationClaim = isolationClaim;
      this.isolateFilteredLoading = true;
      if (this.sessionEmptyMatch) {
        this.refuseSpatialFocus('spatialScopeUnavailable', 'No records match the current grid filters.', isolationClaim);
        this.isolateFilteredLoading = false;
        return;
      }
      if (this.frozen) {
        this.refuseSpatialFocus('spatialScopeUnavailable', 'Clear the active selection before isolating this reference grid.', isolationClaim);
        this.isolateFilteredLoading = false;
        return;
      }

      try {
        const spatial = await this.ensureSpatialCapability();
        if (sequence !== this.filteredSpatialSeq || signal.aborted || !this.canWriteIsolation(isolationClaim)) return;
        if (!hasExactSpatialCapability(spatial)) {
          return this.refuseSpatialFocus('spatialCapabilityMissing', 'This data source does not publish an exact 3D destination.', isolationClaim);
        }
        const globalOnly = !this.filterSessionId
          && !!(this.globalFilters && Object.keys(this.globalFilters).length);
        if (globalOnly) {
          return this.refuseSpatialFocus('spatialGlobalFiltersUnsupported', 'The current filters are not ready for the 3D view.', isolationClaim);
        }
        const scope = this.buildFilteredSpatialScope(spatial);
        if (!scope) {
          return this.refuseSpatialFocus('spatialScopeUnavailable', 'The filtered grid scope could not be prepared for 3D.', isolationClaim);
        }
        const envelope = buildSpatialResolveRequest({
          scope,
          focus: { spatialContractVersion: 2, mode: 'source' },
        });
        if (envelope.error) {
          warn('SqlViewDataGrid', 'filtered 3D request refused', envelope.error);
          return this.refuseSpatialFocus(envelope.error.code, envelope.error.message, isolationClaim);
        }
        const response = await apiService.resolveSpatialFocus(envelope.request, { signal });
        if (sequence !== this.filteredSpatialSeq || signal.aborted || !this.canWriteIsolation(isolationClaim)) return;
        const result = response?.data || {};
        if (!hasDisplayTarget(result)) {
          return this.refuseSpatialFocus('spatialNoDisplayTargets', 'No filtered records have an available 3D location.', isolationClaim);
        }
        this.pendingFilteredSpatial = {
          operation,
          scope,
          spatial,
          resolvedResult: result,
          fingerprint: this.filteredSpatialFingerprint(scope),
        };
        this.$store.commit('SET_VIEWER_TYPE', '3d');
        this.$emit('openViewer', operation);
      } catch (err) {
        if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError' || err.__CANCEL__)) return;
        if (sequence !== this.filteredSpatialSeq || !this.canWriteIsolation(isolationClaim)) return;
        error('SqlViewDataGrid', 'filtered 3D preflight failed', err);
        this.refuseSpatialFocus('spatialPreflightFailed', err?.message || 'The filtered records could not be checked for 3D locations.', isolationClaim);
      } finally {
        if (sequence === this.filteredSpatialSeq) this.isolateFilteredLoading = false;
      }
    },

    invalidateFilteredSpatial() {
      if (this._filteredSpatialController) this._filteredSpatialController.abort();
      this._filteredSpatialController = null;
      this.filteredSpatialSeq += 1;
      this.isolateFilteredLoading = false;
      this.pendingFilteredSpatial = null;
      if (['none', 'relatedOnly'].includes(this.loadedSpatialEligibility) && this.sourceSpatialStatus === 'available') {
        this.setSourceSpatialStatus('available');
      }
      this.loadedSpatialEligibility = 'unknown';
    },

    async showPreparedFilteredSpatial(operation, isolationClaim = this.captureIsolationClaim()) {
      const prepared = this.pendingFilteredSpatial;
      if (!prepared || prepared.operation?.token !== operation?.token) return false;
      this.pendingFilteredSpatial = null;
      const currentScope = this.buildFilteredSpatialScope(prepared.spatial);
      if (this.filteredSpatialFingerprint(currentScope) !== prepared.fingerprint) {
        this.invalidateFilteredSpatial();
        return this.refuseSpatialFocus('spatialScopeUnavailable', 'The grid filters changed before the 3D view opened.', isolationClaim);
      }
      return this.showExactSpatialFocus(
        prepared.scope,
        prepared.spatial,
        isolationClaim,
        'source',
        prepared.resolvedResult,
      );
    },

    /** Toolbar action: open the 3D viewer and isolate EVERY currently-filtered row's tag(s)
     *  at once. Rides the same openViewer handshake as the single-row isolate; the
     *  pendingIsolateAll flag routes the post-load callback. Works with or without a colouring
     *  column — the target is the filtered row set, colouring is an optional overlay. */
    isolateAllRows() {
      const operation = this.nextIsolationOperation('isolateAll');
      this.$store.commit('SET_VIEWER_TYPE', '3d');
      this.$emit('openViewer', operation);
    },

    /**
     * Isolate every currently-scoped row.
     *
     * Which path answers is decided from the source's own capability, before any request:
     *   capable     -> one exact `query` scope through `useSpatialFocus`. It covers every matching
     *                  row (never just the loaded AG Grid page) and, unlike the old action, it
     *                  carries the grid's ROW WINDOW, so a Top-10 grid isolates exactly ten.
     *   not capable -> clear the previous scene and report the missing source contract.
     */
    async isolateAllFilteredRows(isolationClaim = this.captureIsolationClaim()) {
      // Empty-match: the active chart selection matches zero rows (the grid renders an empty
      // page locally), so there is nothing to isolate — and the 0-tag session reads as expired
      // server-side, which would resolve the UNFILTERED tag set. Refuse instead of isolating
      // the whole facility.
      if (this.sessionEmptyMatch) {
        this.$dialog?.notify?.warning('No rows match the current chart selection.', { position: 'top-right', timeout: 3000 });
        return;
      }
      const viewer = this.$store.state.viewer;
      const extension = this.$store.state.forgeService?.getMainExtension();
      if (!viewer || !extension) return;

      const tree = viewer.model?.getInstanceTree();
      if (!tree) {
        this.$dialog?.notify?.warning('Model not loaded yet.', { position: 'top-right', timeout: 3000 });
        return;
      }

      const spatial = await this.ensureSpatialCapability();
      if (!this.canWriteIsolation(isolationClaim)) return;
      // A raw global filter cannot be stated as a query scope — the server rejects the field
      // outright — so dropping it would resolve a wider row set than the grid displays.
      const globalOnly = !this.filterSessionId
        && !!(this.globalFilters && Object.keys(this.globalFilters).length);
      if (!hasExactSpatialCapability(spatial)) {
        return this.refuseSpatialFocus(
          'spatialCapabilityMissing',
          'This data source has no published spatial target or context. Configure its Spatial Focus roles and republish it.',
          isolationClaim,
        );
      }
      // The query arm names rows by PREDICATE, so unlike the single-row path it needs no
      // row-carried key — the resolver adds its own provenance columns server-side.
      if (globalOnly) {
        return this.refuseSpatialFocus(
          'spatialGlobalFiltersUnsupported',
          'This filtered selection cannot be shown exactly in 3D until the dashboard filter session is available. Refresh the dashboard and try again.',
          isolationClaim,
        );
      }
      const scope = buildSpatialQueryScope({
        tableName: this.sqlViewConfig.tableName,
        columns: this.sqlViewConfig.columns,
        filters: this.mergedFilters,
        search: this.searchText,
        filterSessionId: this.filterSessionId,
        expectedSourceRevision: spatial.sourceRevision,
        encodingColumn: this.hasColoring ? this.coloringColumn : null,
        // The grid displays a ranked slice, so the focus must cover exactly that slice.
        rowWindow: this.rowWindow,
      });
      if (scope) return this.showExactSpatialFocus(scope, spatial, isolationClaim);
      this.reportExactPathShortfall('rows', 'the current scope could not be stated exactly', {
        tableName: this.sqlViewConfig.tableName,
      });
      return this.refuseSpatialFocus(
        'spatialScopeUnavailable',
        'This filtered selection could not be represented as an exact Spatial Focus scope.',
        isolationClaim,
      );
    },

    // =====================================================================================
    // =====================================================================================
    // Exact spatial focus — the capability gate
    // =====================================================================================
    // The source's own catalogue `spatial` descriptor is checked BEFORE any request goes out. A
    // capable source hands one exact scope to `useSpatialFocus`; a non-capable source clears the
    // previous scene and reports the missing contract. No dashboard gesture can reach a whole-tag
    // resolver or create a second render authority.

    /**
     * Resolve (once) this widget's server-owned spatial capability from the dashboard catalogue.
     *
     * One EXISTING GET (`apiService.tables()`); the entry already carries `spatial`. A widget saved
     * before the contract existed hydrates its runtime descriptor from the catalogue here — no API
     * write occurs, so persistence stays owned by the editor's save lifecycle. A stale saved
     * revision makes the source non-capable rather than resolving rows the widget never displayed.
     * Memoised so concurrent isolates share one fetch. ONLY a capability is memoised: a null is
     * never terminal, because a source GAINS one the moment it is republished with a spatial
     * contract, and caching the pre-republish answer for the life of the component left the widget
     * in a refused state until a full page reload. A null therefore forgets itself, so the next
     * isolate gesture re-reads the catalogue — one GET per gesture on a source that stays
     * non-capable, versus a wrong answer forever.
     * @returns {Promise<?object>} `TableViewInfo.spatial`
     */
    loadSourceSpatialCapability() {
      if (!this.link3D) {
        this.setSourceSpatialStatus('unavailable', 'notLinked');
        return Promise.resolve(null);
      }
      return this.ensureSpatialCapability();
    },

    resetSourceSpatialCapability() {
      this.spatialCapabilitySeq += 1;
      this._spatialPromise = null;
      this.hydratedSpatial = null;
      this.loadedSpatialEligibility = 'unknown';
      this.setSourceSpatialStatus('unknown');
      this.loadSourceSpatialCapability();
    },

    setSourceSpatialStatus(status, reason = null) {
      this.sourceSpatialStatus = status;
      this.$emit?.('spatialCapability', { status, ...(reason ? { reason } : {}) });
    },

    ensureSpatialCapability() {
      if (this.hydratedSpatial) {
        this.setSourceSpatialStatus('available');
        return Promise.resolve(this.hydratedSpatial);
      }
      if (this._spatialPromise) return this._spatialPromise;
      // Answered WITHOUT memoising: a widget whose config hydrates after its first isolate would
      // otherwise be pinned by an answer taken before it had a table to ask about.
      const tableName = this.sqlViewConfig?.tableName;
      if (!tableName) {
        this.setSourceSpatialStatus('unknown');
        return Promise.resolve(null);
      }
      this.setSourceSpatialStatus('loading');
      const sequence = this.spatialCapabilitySeq;
      // `forget` runs after the promise resolves, so it must never fire before the assignment below
      // completes — every use of it is behind an `await`.
      const forget = (value) => {
        if (sequence === this.spatialCapabilitySeq) this._spatialPromise = null;
        return value;
      };
      this._spatialPromise = (async () => {
        try {
          const viewsRes = await apiService.tables();
          if (sequence !== this.spatialCapabilitySeq) return null;
          const views = Array.isArray(viewsRes?.data) ? viewsRes.data : [];
          const match = views.find(
            (v) => String(v?.name).toLowerCase() === String(tableName).toLowerCase(),
          );
          if (!match) {
            this.setSourceSpatialStatus('unknown');
            return forget(null);
          }
          const currentSpatial = match.spatial || null;
          if (!hasExactSpatialCapability(currentSpatial)) {
            log('SqlViewDataGrid', 'catalogue has no spatial capability — refusing spatial focus', { tableName });
            this.setSourceSpatialStatus('unavailable', 'sourceUnsupported');
            return forget(null);
          }
          // Always use the CURRENT capability. Widgets persist only binding intent; an override's
          // own sourceRevision is checked by resolveSpatialFocusPolicy below.
          this.hydratedSpatial = currentSpatial;
          this.setSourceSpatialStatus('available');
          this.initColumns();
          if (this.gridApi) this.setDatasource();
          return currentSpatial;
        } catch (err) {
          error('SqlViewDataGrid', 'spatial capability lookup failed — refusing spatial focus', err);
          if (sequence === this.spatialCapabilitySeq) {
            this._spatialPromise = null; // let a later isolate retry
            this.setSourceSpatialStatus('unknown');
          }
          return null;
        }
      })();
      return this._spatialPromise;
    },

    /** The widget's live value -> colour definition, or a bare column when nothing is configured. */
    focusEncoding() {
      const column = this.coloringColumn;
      if (!column) return { column: null, displayName: null };
      const valueColors = this.valueColors;
      return {
        column,
        displayName: this.getColumnDisplayName(column),
        colorFor: (value) => valueColors[String(value)] || UNMAPPED_ISOLATE_COLOR,
      };
    },

    /** The scene owner: the widget, identified by id and the title the legend heads with. */
    focusOwner() {
      return {
        widgetId: this.message?.id ?? null,
        title: this.message?.title || this.sqlViewConfig?.title || 'Isolated rows',
      };
    },

    /**
     * Render one typed outcome. `empty` and `unrenderable` are DIFFERENT failures, read from the
     * status the composable reported rather than re-derived from the counts: the first matched no
     * rows, the second matched rows and then lost every one of them at the viewer.
     * @returns {boolean} whether the gesture was answered here (false = a newer caller owns it)
     */
    applyFocusOutcome(outcome) {
      const status = outcome && outcome.status;
      if (status === 'rendered') return true;
      // A newer caller owns the scene; leaving it alone is the correct answer.
      if (status === 'aborted' || status === 'superseded') return true;

      const readerMessage = focusOutcomeMessage(outcome);
      if (readerMessage) {
        this.$store.commit('SET_COLORING_LEGEND', {
          owner: this.focusOwner(), notice: readerMessage, entries: [],
        });
        return true;
      }

      return true;
    },

    /**
     * A capable source that could NOT be stated as an exact scope.
     *
     * This is the one shortfall the exact path can hit after the capability gate already said yes.
     * It is logged once with the missing contract detail; `refuseSpatialFocus` owns the visible
     * fail-closed outcome and clears the previous scene.
     * @param {string} kind 'row' | 'rows'
     * @param {string} reason human-readable, already naming the column
     * @param {object} details logged context
     */
    reportExactPathShortfall(kind, reason, details) {
      warn('SqlViewDataGrid', `exact ${kind} focus unavailable — refusing non-spatial isolation`, {
        ...details,
        reason,
      });
    },

    /**
     * Fail one dashboard isolation gesture closed when its source cannot author an exact scene.
     * The shared composable owns markers, theming, pulse and isolation as one ledger, so clearing
     * through that same authority is the only safe transition from a previous exact focus. A
     * whole-tag resolve here would create a second authority and can leave the old marker/paint
     * attached to a camera move from the new gesture.
     */
    refuseSpatialFocus(reason, message, isolationClaim = this.captureIsolationClaim()) {
      if (!this.canWriteIsolation(isolationClaim)) return true;
      warn('SqlViewDataGrid', 'spatial focus refused', { reason, message });
      const viewer = this.$store.state.viewer;
      if (viewer) this.focus.clear({ viewer, reason });
      const readerMessage = spatialRefusalMessage(reason);
      this.$store.commit('SET_COLORING_LEGEND', {
        owner: this.focusOwner(), notice: readerMessage, entries: [],
      });
      this.$dialog?.notify?.warning(readerMessage, { position: 'top-right', timeout: 4500 });
      return true;
    },

    /**
     * Hand one exact scope to the composable.
     *
     * The grid supplies INTENT only — which rows, who owns the scene, and how a value becomes a
     * colour. It supplies no dbIds, no gray context, no target precedence and no legend counts, and
     * it never walks its own loaded AG Grid nodes looking for geometry: the scope covers every
     * matching row, not just the rendered page.
     */
    async showExactSpatialFocus(scope, spatial, isolationClaim, displayMode = 'source', resolvedResult = null) {
      const viewer = this.$store.state.viewer;
      if (!viewer || !scope) return false;
      if (!this.canWriteIsolation(isolationClaim)) return true;
      const widgetFocus = this.sqlViewConfig?.spatialFocus
        || legacySpatialFocusOverride(spatial, this.sqlViewConfig);
      // A preflight receipt was already resolved with the exact source-v2 envelope. Do not let an
      // obsolete saved widget override veto that validated result while the viewer is opening.
      const policy = resolvedResult
        ? { legacy: false, error: null, focus: { spatialContractVersion: 2, mode: 'source' } }
        : resolveSpatialFocusSessionPolicy(
          spatial,
          widgetFocus,
          this._spatialFocusSessionOverride,
        );
      if (policy.error) {
        return this.refuseSpatialFocus(policy.error.code, policy.error.message, isolationClaim);
      }
      const focus = resolvedResult || scope.kind === 'rows'
        ? { spatialContractVersion: 2, mode: 'source' } : policy.focus;
      const envelope = buildSpatialResolveRequest({ scope, focus });
      if (envelope.error) {
        return this.refuseSpatialFocus(envelope.error.code, envelope.error.message, isolationClaim);
      }
      if (!policy.legacy && this._onSpatialFocusFallbackResolved) {
        this._onSpatialFocusFallbackResolved(spatialFocusFallbackEnabled(
          spatial,
          widgetFocus,
          this._spatialFocusSessionOverride,
        ));
      }
      this._lastTypedSpatialFocus = policy.legacy
        ? null
        : {
          scope,
          spatial,
          isolationClaim: isolationClaim ? { ...isolationClaim } : null,
          displayMode,
          resolvedResult,
        };
      // Drop theming that predates the unified focus adapter. The composable's clear intentionally
      // reaches only its own ledger, so this compatibility cleanup prevents an older in-memory
      // scene from surviving under the first exact one.
      this.clearStaleTheming(isolationClaim);
      if (!this.canWriteIsolation(isolationClaim)) return true;
      const outcome = await this.focus.show({
        owner: this.focusOwner(),
        request: envelope.request,
        displayMode,
        scope,
        encoding: this.focusEncoding(),
        viewer,
        resolvedResult,
      });
      if (!this.canWriteIsolation(isolationClaim)) return true;
      return this.applyFocusOutcome(outcome);
    },

    setSpatialFocusSessionOverride(value, onResolved = null) {
      this._spatialFocusSessionOverride = typeof value === 'boolean' ? value : null;
      this._onSpatialFocusFallbackResolved = typeof onResolved === 'function' ? onResolved : null;
    },

    async replaySpatialFocusForFallback() {
      const last = this._lastTypedSpatialFocus;
      if (!last || !this.canWriteIsolation(last.isolationClaim)) return false;
      await this.showExactSpatialFocus(last.scope, last.spatial, last.isolationClaim, last.displayMode, last.resolvedResult);
      return true;
    },


    nextIsolationOperation(kind) {
      const operation = { kind, token: this.isolationIntentSeq + 1 };
      this.isolationIntentSeq = operation.token;
      if (kind === 'isolateAll') {
        this.pendingIsolateAll = true;
        this.pendingIsolateAllToken = operation.token;
      } else {
        this.pendingIsolateAll = false;
        this.pendingIsolateAllToken = null;
      }
      if (kind !== 'filteredSpatial') this.pendingFilteredSpatial = null;
      return operation;
    },

    cancelPendingIsolation(operation = null) {
      if (operation?.kind === 'isolateAll' && this.pendingIsolateAllToken !== operation.token) return;
      if (operation?.kind === 'filteredSpatial' && this.pendingFilteredSpatial?.operation?.token !== operation.token) return;
      if (operation && operation.kind !== 'isolateAll' && operation.kind !== 'filteredSpatial') return;
      this.pendingIsolateAll = false;
      this.pendingIsolateAllToken = null;
      this.pendingFilteredSpatial = null;
    },

    /** Drop any theming left by a previous coloured isolate so a plain isolate shows true colours. */
    clearStaleTheming(isolationClaim = this.captureIsolationClaim()) {
      if (!this.canWriteIsolation(isolationClaim)) return;
      const viewer = this.$store.state.viewer;
      const themed = this.$store.getters['GET_THEMED_DB_IDS'];
      if (themed?.length && viewer) clearThemingColors(viewer);
    },
  },

  watch: {
    'message.content': {
      handler() {
        this.invalidateFilteredSpatial();
        this.initColumns();
        if (this.gridApi) {
          this.setDatasource();
          this.$nextTick(() => this.restoreColumnState());
        }
      },
      deep: true,
    },
    // Cross-filter refetches go through the SOFT path: rows stay mounted under a scrim +
    // progress strip instead of dropping to the rows skeleton (the grid is already rendered;
    // only the indicator should show).
    filterSessionId() {
      this.invalidateFilteredSpatial();
      if (this.gridApi) {
        this.scheduleSoftRefresh();
      }
    },
    // Empty-match flip (decision 10): re-run the datasource so the grid switches to/from the
    // empty page when the combined selection starts/stops matching nothing.
    sessionEmptyMatch() {
      this.invalidateFilteredSpatial();
      if (this.gridApi) {
        this.scheduleSoftRefresh();
      }
    },
    // A same-table selection changes this grid's exact row scope with no session change at all,
    // so it needs its own refetch trigger. Coalesced with the session/empty-match watchers, since
    // one selection commit can move several of them in the same tick.
    selectionFilters() {
      this.invalidateFilteredSpatial();
      if (this.gridApi) {
        this.scheduleSoftRefresh();
      }
    },
    globalFilters: {
      handler() {
        this.invalidateFilteredSpatial();
        if (this.gridApi) {
          this.scheduleSoftRefresh();
        }
      },
      deep: true,
    },
    widgetFilters: {
      handler() {
        this.invalidateFilteredSpatial();
        if (this.gridApi) {
          this.scheduleSoftRefresh();
        }
      },
      deep: true,
    },
    sqlViewConfig: {
      handler() {
        this.invalidateFilteredSpatial();
      },
      deep: true,
    },
    spatialSourceKey() {
      this.resetSourceSpatialCapability();
    },
  },
};
</script>

<style lang="scss" scoped>
.sql-view-data-grid {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sql-view-ag-grid-stack {
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  min-height: 0;
}

/* Structured (dashboard) grid: grow to fill the widget body via flex instead of a JS px height,
   so the grid tracks the card exactly and only ever scrolls internally (min-height:0 inherited
   from the base rule lets it shrink below content size). */
.sql-view-ag-grid-stack--structured {
  flex: 1 1 auto;
}

.sql-view-ag-grid-stack .sql-view-ag-grid {
  width: 100%;
  height: 100%;
}

.sql-grid-skeleton-overlay {
  position: absolute;
  z-index: 5;
  top: 49px;
  right: 0;
  bottom: 32px;
  left: 0;
  width: auto;
  height: auto;
  pointer-events: none;
  background: var(--sql-register-surface, var(--ds-surface-elevated, #fcfdfc));
}

.sql-view-ag-grid-stack--structured .sql-grid-skeleton-overlay {
  top: 46px;
  bottom: 44px;
}

/* Soft refresh (cross-filter refetch with rows retained): same restrained scrim + top progress
   strip the chart widgets use (cdb-refresh-scrim/-bar values), scoped to the grid stack. */
.svdg-refresh-scrim {
  position: absolute;
  inset: 0;
  z-index: 6;
  background: rgba(252, 253, 252, 0.45);
  pointer-events: auto;
}
.theme--dark .svdg-refresh-scrim {
  background: rgba(24, 28, 27, 0.42);
}
.svdg-refresh-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 7;
}

.sql-view-data-grid--dashboard {
  container-type: inline-size;
  --sql-register-surface: #fcfdfc;
  --sql-register-surface-soft: #f6f7f6;
  --sql-register-surface-hover: #ecefed;
  --sql-register-ink: #29312e;
  --sql-register-ink-secondary: #52605b;
  --sql-register-ink-muted: #75807c;
  --sql-register-line: rgba(27, 52, 45, 0.14);
  --sql-register-line-strong: rgba(27, 52, 45, 0.28);
  --sql-register-primary: #03624c;
  box-sizing: border-box;
  overflow: hidden;
  background: var(--sql-register-surface);
}

.theme--dark .sql-view-data-grid--dashboard {
  --sql-register-surface: #202724;
  --sql-register-surface-soft: #252e2a;
  --sql-register-surface-hover: #2b3732;
  --sql-register-ink: #f0f5f3;
  --sql-register-ink-secondary: #bcc9c4;
  --sql-register-ink-muted: #91a19b;
  --sql-register-line: rgba(232, 245, 239, 0.12);
  --sql-register-line-strong: rgba(232, 245, 239, 0.26);
  --sql-register-primary: #2cc295;
}

.sql-view-data-grid-toolbar {
  flex-shrink: 0;
}

</style>

<style lang="scss">
.sql-view-ag-grid .ag-icon {
  font-family: "agGridAlpine" !important;
}

/* Custom Dashboard Structured Register toolbar. */
.sql-view-data-grid--dashboard .sql-grid-operations {
  min-height: 67px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  padding: 12px 13px;
  border-bottom: 1px solid var(--sql-register-line);
  background: var(--sql-register-surface);
}

.sql-view-data-grid--dashboard .sql-grid-search {
  min-width: 120px;
  max-width: none;
  flex: 1 1 auto;
  margin: 0;
  padding: 0;
}

.sql-view-data-grid--dashboard .sql-grid-search .v-input__slot {
  min-height: 42px !important;
  margin: 0;
  border-radius: 6px;
  background: var(--sql-register-surface) !important;
  box-shadow: none !important;
}

.sql-view-data-grid--dashboard .sql-grid-search.v-text-field--outlined fieldset {
  border-color: var(--sql-register-line-strong);
}

.sql-view-data-grid--dashboard .sql-grid-search input {
  color: var(--sql-register-ink);
  font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  font-size: 14px;
}

.sql-view-data-grid--dashboard .sql-grid-search input::placeholder,
.sql-view-data-grid--dashboard .sql-grid-search .v-icon {
  color: var(--sql-register-ink-muted) !important;
  opacity: 1;
}

.sql-view-data-grid--dashboard .sql-grid-search .v-icon {
  font-size: 19px !important;
}

.sql-view-data-grid--dashboard .sql-grid-search.v-input--is-focused fieldset {
  border-color: var(--v-primary-base, #03624c) !important;
  box-shadow: 0 0 0 2px rgba(3, 98, 76, 0.14);
}

.sql-view-data-grid--dashboard .sql-grid-row-hint.v-btn {
  min-width: 34px;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  margin: 0;
  color: var(--sql-register-ink-muted);
}

.sql-view-data-grid--dashboard .sql-grid-row-hint.v-btn:hover,
.sql-view-data-grid--dashboard .sql-grid-row-hint.v-btn:focus-visible {
  background: var(--sql-register-surface-hover);
  color: var(--sql-register-ink-secondary);
}

.sql-view-data-grid--dashboard .sql-grid-row-hint.v-btn .v-icon {
  color: currentColor;
}

.sql-view-data-grid--dashboard .sql-grid-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
}

.sql-view-data-grid--dashboard .sql-grid-toolbar-btn.v-btn {
  min-width: 42px;
  height: 42px;
  padding: 0 11px;
  border-color: var(--sql-register-line);
  border-radius: 6px;
  color: var(--sql-register-ink-secondary);
  font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.sql-view-data-grid--dashboard .sql-grid-toolbar-btn.v-btn:hover,
.sql-view-data-grid--dashboard .sql-grid-toolbar-btn.v-btn:focus-visible {
  border-color: var(--sql-register-line-strong);
  background: var(--sql-register-surface-hover);
  color: var(--sql-register-ink);
}

.sql-view-data-grid--dashboard .sql-grid-isolate-btn.v-btn {
  border-color: var(--sql-register-primary);
  background: rgba(3, 98, 76, 0.08);
  color: var(--sql-register-primary);
}

.sql-view-data-grid--dashboard .sql-grid-isolate-btn.v-btn:hover,
.sql-view-data-grid--dashboard .sql-grid-isolate-btn.v-btn:focus-visible {
  border-color: var(--sql-register-primary);
  background: rgba(3, 98, 76, 0.14);
  color: var(--sql-register-primary);
}

.theme--dark .sql-view-data-grid--dashboard .sql-grid-isolate-btn.v-btn {
  background: rgba(44, 194, 149, 0.12);
}

.sql-view-data-grid--dashboard .sql-grid-toolbar-btn .v-icon {
  color: inherit !important;
  font-size: 18px !important;
}

.sql-view-data-grid--dashboard .sql-grid-icon-btn.v-btn {
  width: 42px;
  padding: 0;
}

.sql-view-data-grid--dashboard .sql-grid-action-label {
  margin-left: 7px;
  white-space: nowrap;
}

.sql-view-data-grid--dashboard .sql-grid-filter-count {
  min-width: 32px;
  height: 32px;
  display: inline-grid;
  place-items: center;
  margin-left: 10px;
  border-radius: 999px;
  padding: 0 9px;
  background: var(--v-primary-base, #03624c);
  color: #f1fff9;
  font-size: 17px;
  line-height: 1;
}

.sql-view-data-grid--dashboard .sql-grid-queries-btn {
  position: relative;
}

.sql-view-data-grid--dashboard .sql-grid-queries-count {
  position: absolute;
  top: -16px;
  right: -16px;
  min-width: 18px;
  height: 18px;
  display: inline-grid;
  place-items: center;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--sql-register-primary);
  color: #f1fff9;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

/* Queries menu content is teleported outside .sql-view-data-grid--dashboard, so it cannot see
   the register variables — it rides Vuetify's themed v-list defaults, with only wrap/spacing
   tweaks here so long hints and the empty state read cleanly. */
.sql-grid-queries-menu .sql-grid-queries-subheader {
  height: 34px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sql-grid-queries-menu .v-list-item__subtitle,
.sql-grid-queries-menu .sql-grid-queries-empty {
  white-space: normal;
}

.sql-grid-queries-menu .sql-grid-queries-lock {
  opacity: 0.6;
}

/* AG Grid Structured Register treatment. */
.sql-view-ag-grid--structured {
  --ag-font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  --ag-font-size: 12px;
  --ag-grid-size: 6px;
  --ag-row-height: 50px;
  --ag-header-height: 45px;
  --ag-border-radius: 0;
  --ag-borders: none;
  --ag-border-color: var(--sql-register-line);
  --ag-secondary-border-color: var(--sql-register-line);
  --ag-row-border-color: var(--sql-register-line);
  --ag-row-border-width: 1px;
  --ag-row-border-style: solid;
  --ag-cell-horizontal-border: solid 1px var(--sql-register-line);
  --ag-background-color: var(--sql-register-surface);
  --ag-foreground-color: var(--sql-register-ink-secondary);
  --ag-secondary-foreground-color: var(--sql-register-ink-muted);
  --ag-header-background-color: var(--sql-register-surface-soft);
  --ag-header-foreground-color: var(--sql-register-ink-secondary);
  --ag-odd-row-background-color: var(--sql-register-surface);
  --ag-row-hover-color: var(--sql-register-surface-hover);
  --ag-selected-row-background-color: transparent;
  --ag-range-selection-border-color: var(--sql-register-primary);
  --ag-input-focus-border-color: var(--sql-register-primary);
  --ag-header-column-separator-display: block;
  --ag-header-column-separator-color: var(--sql-register-line);
  --ag-header-column-separator-height: 100%;
  --ag-header-column-separator-width: 1px;
  --ag-header-column-resize-handle-color: var(--sql-register-primary);
  --ag-header-column-resize-handle-height: 48%;
  --ag-cell-horizontal-padding: 11px;
}

/* Widget size - datagrid density packs. The base block above IS the large pack (12/50/45/11px/6,
   today's verbatim), so large carries no class and stays byte-identical. Normal and small override
   the font, grid-size, row/header height and cell padding; the matching AG rowHeight/headerHeight
   props (sizeRowHeight/sizeHeaderHeight) keep the JS-driven row sizing in step, and AG derives the
   line-height from rowHeight. Compounded with .sql-view-ag-grid--structured so they apply only in the
   dashboard (structured) treatment. */
.sql-view-ag-grid--structured.sql-view-ag-grid-size--normal {
  --ag-font-size: 11px;
  --ag-grid-size: 5px;
  --ag-row-height: 41px;
  --ag-header-height: 38px;
  --ag-cell-horizontal-padding: 9px;
}
.sql-view-ag-grid--structured.sql-view-ag-grid-size--small {
  --ag-font-size: 10px;
  --ag-grid-size: 4px;
  --ag-row-height: 33px;
  --ag-header-height: 32px;
  --ag-cell-horizontal-padding: 7px;
}

.sql-view-ag-grid--structured .ag-root-wrapper {
  box-sizing: border-box;
  border: 0 !important;
  border-radius: 0;
  /* DashboardGrid.vue ships an UNSCOPED .ag-root-wrapper { min-height: 400px } that leaks onto
     every AG Grid in the app; inside a flex-filled dashboard widget that floor forces the grid
     past the card (the original bleed bug). The widget body is the only height authority here. */
  min-height: 0;
}

.sql-view-ag-grid--structured .ag-root.ag-layout-normal {
  border: 0 !important;
}

.sql-view-ag-grid--structured .ag-pinned-left-header .sql-grid-viewer-header,
.sql-view-ag-grid--structured .sql-grid-viewer-header {
  padding-right: 0 !important;
  padding-left: 0 !important;
  border-right: 0 !important;
}

.sql-view-ag-grid--structured .sql-grid-viewer-header .ag-header-cell-label {
  justify-content: center;
}

.sql-view-ag-grid--structured .ag-pinned-left-header,
.sql-view-ag-grid--structured .ag-pinned-left-cols-container {
  border-right: 1px solid var(--sql-register-line-strong);
}

.sql-view-ag-grid--structured .ag-header,
.sql-view-ag-grid--structured .ag-header-row {
  background: var(--sql-register-surface-soft) !important;
}

.sql-view-ag-grid--structured .ag-header {
  border-bottom: 1px solid var(--sql-register-line-strong) !important;
}

.sql-view-ag-grid--structured .ag-row {
  background: var(--sql-register-surface) !important;
  border-bottom: 1px solid var(--sql-register-line) !important;
  color: var(--sql-register-ink-secondary);
}

.sql-view-ag-grid--structured .ag-cell,
.sql-view-ag-grid--structured .ag-header-cell {
  border-right: 1px solid var(--sql-register-line) !important;
}

.sql-view-ag-grid--structured .ag-row-hover,
.sql-view-ag-grid--structured .ag-row:hover {
  background: var(--sql-register-surface-hover) !important;
}

.sql-view-ag-grid--structured .ag-header-cell-label {
  font-family: var(--ds-font-family-heading, "Poppins", sans-serif);
  font-size: 11px;
  font-weight: 600;
}

/* CustomDashboard.vue has a legacy all-caps micro-header rule for generic grids. The
   Structured Register uses the showcase's sentence-case Poppins headers instead. */
.cdb-builder-v1 .sql-view-ag-grid--structured .ag-header-cell-text {
  font-family: var(--ds-font-family-heading, "Poppins", sans-serif) !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  color: var(--sql-register-ink-secondary) !important;
}

/* Widget size: header text follows the tiers too (14px pinned above = large/default). */
.cdb-builder-v1 .sql-view-ag-grid--structured.sql-view-ag-grid-size--normal .ag-header-cell-text {
  font-size: 13px !important;
}

.cdb-builder-v1 .sql-view-ag-grid--structured.sql-view-ag-grid-size--small .ag-header-cell-text {
  font-size: 12px !important;
}

.sql-view-ag-grid--structured .ag-header-cell-resize::after {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.sql-view-ag-grid--structured .ag-header-cell:hover .ag-header-cell-resize::after,
.sql-view-ag-grid--structured .ag-header-cell-resize:hover::after {
  opacity: 1;
}

.sql-view-ag-grid--structured .ag-header-cell-sorted-asc .ag-header-cell-text,
.sql-view-ag-grid--structured .ag-header-cell-sorted-desc .ag-header-cell-text,
.sql-view-ag-grid--structured .ag-header-icon {
  color: var(--sql-register-primary);
}

.sql-view-ag-grid--structured .ag-cell {
  display: flex !important;
  align-items: center !important;
  overflow: hidden;
  line-height: normal;
}

.sql-view-ag-grid--structured .ag-cell-wrapper {
  width: 100%;
  height: 100%;
  min-width: 0;
  align-items: center;
}

.sql-view-ag-grid--structured .sql-grid-cell-text {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cdb-builder-v1 .sql-view-ag-grid--structured .ag-cell.sql-grid-data-cell {
  font-family: "Roboto Mono", "SFMono-Regular", Consolas, monospace !important;
  font-size: 16px !important;
  font-variant-numeric: tabular-nums;
}

/* Widget size: the pinned data-cell font above IS the large tier (grid default). The
   normal/small tiers must scale it too, or picking a size only shortens the rows while
   the text stays 16px (looks broken). Same selector + size class = later + more specific. */
.cdb-builder-v1 .sql-view-ag-grid--structured.sql-view-ag-grid-size--normal .ag-cell.sql-grid-data-cell {
  font-size: 14px !important;
}

.cdb-builder-v1 .sql-view-ag-grid--structured.sql-view-ag-grid-size--small .ag-cell.sql-grid-data-cell {
  font-size: 12px !important;
}

.sql-view-ag-grid--structured .ag-cell.sql-grid-number-cell {
  justify-content: flex-end;
  text-align: right;
}

.sql-view-ag-grid--structured .ag-cell.sql-grid-viewer-cell {
  justify-content: center;
  padding-inline: 0;
  border-right: 0 !important;
}

.sql-view-ag-grid--structured .sql-grid-viewer-cell .v-btn {
  width: 28px;
  height: 28px;
  border: 1px solid var(--sql-register-primary);
  border-radius: 6px;
  background: rgba(3, 98, 76, 0.08);
  color: var(--sql-register-primary) !important;
  opacity: 1;
}

.sql-view-ag-grid--structured .sql-grid-viewer-cell .v-icon {
  font-size: 18px !important;
  color: inherit !important;
}

.sql-view-ag-grid--structured .sql-grid-viewer-cell .v-btn:hover,
.sql-view-ag-grid--structured .sql-grid-viewer-cell .v-btn:focus-visible {
  border-color: var(--sql-register-primary);
  background: rgba(3, 98, 76, 0.16);
  color: var(--sql-register-primary) !important;
}

.theme--dark .sql-view-ag-grid--structured .sql-grid-viewer-cell .v-btn {
  background: rgba(44, 194, 149, 0.12);
}

.sql-view-ag-grid--structured .ag-paging-panel {
  height: 44px !important;
  min-height: 44px;
  border-top: 1px solid var(--sql-register-line);
  color: var(--sql-register-ink-muted);
  font-size: 11px;
}

.sql-view-ag-grid--structured .ag-overlay-no-rows-center {
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.sql-view-ag-grid--structured .sql-grid-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  max-width: 420px;
  padding: 16px 24px;
  text-align: center;
  color: var(--sql-register-ink-secondary);
}

.sql-view-ag-grid--structured .sql-grid-empty-state__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 3px;
  border: 1px solid var(--sql-register-line);
  border-radius: 6px;
  background: var(--sql-register-surface-soft);
  color: var(--sql-register-ink-muted);
  font-size: 21px;
}

.sql-view-ag-grid--structured .sql-grid-empty-state strong {
  font-size: 13px;
  font-weight: 600;
}

.sql-view-ag-grid--structured .sql-grid-empty-state > span:last-child {
  color: var(--sql-register-ink-muted);
  font-size: 11.5px;
  line-height: 1.45;
}

.sql-grid-status-pill {
  max-width: 100%;
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 3px 8px;
  color: var(--v-text-base, rgba(0, 0, 0, 0.58));
  background: var(--ds-surface-hover, rgba(0, 0, 0, 0.04));
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
}

.sql-grid-status-pill__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sql-grid-status-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: currentColor;
}

.sql-grid-status-pill--high {
  color: #a53a32;
  background: rgba(165, 58, 50, 0.10);
}

.sql-grid-status-pill--medium {
  color: #8b6500;
  background: rgba(154, 113, 0, 0.11);
}

.sql-grid-status-pill--low {
  color: #28705a;
  background: rgba(47, 111, 89, 0.11);
}

.sql-grid-status-pill--info {
  color: #4f7896;
  background: rgba(84, 125, 154, 0.12);
}

.theme--dark .sql-grid-status-pill--high { color: #ff8c82; }
.theme--dark .sql-grid-status-pill--medium { color: #e1bc52; }
.theme--dark .sql-grid-status-pill--low { color: #72c8a9; }
.theme--dark .sql-grid-status-pill--info { color: #86b4d2; }

// The pinned 3D column cannot scroll horizontally. AG Grid's left spacer uses
// overflow-x: scroll for alignment, which renders redundant Windows arrow buttons.
// Keep the center-column scrollbar unchanged for columns outside the viewport.
.sql-view-ag-grid .ag-horizontal-left-spacer {
  overflow-x: hidden;
}

/* The operations row stays a single line at every widget width: the search shrinks
   (flex: 1 1 auto, min-width 120px) while the actions keep their natural width, then go
   icon-only below. No wrap breakpoint — wrapping was the source of the two-row bleed. */
@container (max-width: 520px) {
  .sql-view-data-grid--dashboard .sql-grid-action-label {
    display: none;
  }

  .sql-view-data-grid--dashboard .sql-grid-toolbar-btn.v-btn {
    width: 40px;
    padding: 0;
  }
}

@media (pointer: coarse) {
  .sql-view-data-grid--dashboard .sql-grid-row-hint.v-btn,
  .sql-view-data-grid--dashboard .sql-grid-toolbar-btn.v-btn,
  .sql-view-ag-grid--structured .sql-grid-viewer-cell .v-btn {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
